import AmountBreakdown from "./AmountBreakdown";
import { Edit as EditIcon } from "@mui/icons-material";
import {
  Box,
  Chip,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { formatDate } from "common/dates";
import { currencyFormat } from "common/numbers";
import * as support from "components/summary/payments/Payments.support";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import * as staticDataService from "services/staticDataService";
import * as pricingSelectors from "store/selectors/pricing/pricingSelectors";

// Pulls an amount in towards the icon column that follows it
const besideIcon = { pr: 0.5 };

const formatAmount = (value) =>
  value != null ? currencyFormat(value, 2) || "" : "";

// Deleting is offered from within the edit dialog
const EditPaymentButton = ({ payment, onRequestEdit }) => (
  <IconButton
    size={"small"}
    color={"primary"}
    aria-label={"Edit payment"}
    onClick={() => onRequestEdit?.(payment)}
    // Cancels out the button's padding so it fits the line height of the text around it and the row doesn't grow,
    // weighted to the top so the icon lines up with the text
    sx={{ mt: "-6px", mb: "-4px" }}
  >
    <EditIcon fontSize={"small"} />
  </IconButton>
);

const EntryRow = ({ row, onRequestEdit }) => (
  <TableRow
    sx={
      row.payment
        ? { backgroundColor: (theme) => theme.brand.accent[200] }
        : undefined
    }
  >
    <TableCell>{row.date ? formatDate(row.date) : ""}</TableCell>
    <TableCell sx={{ whiteSpace: "pre-line" }}>{row.description}</TableCell>
    <TableCell align={"right"} className={"tabular"}>
      {formatAmount(row.due)}
    </TableCell>
    <TableCell sx={{ px: 0 }}>
      {row.breakdown && <AmountBreakdown breakdown={row.breakdown} />}
    </TableCell>
    <TableCell align={"right"} className={"tabular"} sx={besideIcon}>
      {formatAmount(row.received)}
    </TableCell>
    <TableCell sx={{ px: 0 }}>
      {row.payment && (
        <EditPaymentButton
          payment={row.payment}
          onRequestEdit={onRequestEdit}
        />
      )}
    </TableCell>
    <TableCell align={"right"} className={"tabular"}>
      {formatAmount(row.balance)}
    </TableCell>
    <TableCell align={"center"}>{row.type}</TableCell>
  </TableRow>
);

const TotalsRow = ({ totals, status }) => (
  <TableRow sx={{ backgroundColor: "#fafafa", "& td": { fontWeight: 600 } }}>
    <TableCell>{"Total"}</TableCell>
    <TableCell />
    <TableCell align={"right"} className={"tabular"}>
      {formatAmount(totals.due)}
    </TableCell>
    <TableCell sx={{ px: 0 }} />
    <TableCell align={"right"} className={"tabular"} sx={besideIcon}>
      {formatAmount(totals.received)}
    </TableCell>
    <TableCell sx={{ px: 0 }} />
    <TableCell align={"right"} className={"tabular"}>
      {formatAmount(totals.balance)}
    </TableCell>
    <TableCell align={"center"}>
      {status && (
        <Chip
          label={status.value}
          color={status.chipColor}
          size={"small"}
          // Shrunk to the line height of the text around it so the row doesn't grow
          sx={{ height: 20, fontSize: "0.75rem" }}
        />
      )}
    </TableCell>
  </TableRow>
);

const EmptyLedger = () => (
  <Paper variant={"outlined"} sx={{ py: 6, textAlign: "center" }}>
    <Typography variant={"body2"} color={"text.secondary"}>
      {"No payments due or received"}
    </Typography>
  </Paper>
);

const LayerLedger = ({ title, group, onRequestEdit }) => (
  <Box>
    <Typography variant={"subtitle2"} sx={{ pb: 1 }}>
      {title}
    </Typography>
    <TableContainer component={Paper} variant={"outlined"}>
      <Table
        size={"small"}
        aria-label={title}
        sx={{
          "& td, & th": { verticalAlign: "top" },
          // The container's border closes off the table, so the last row doesn't need its own
          "& tbody tr:last-child td": { borderBottom: 0 },
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: "8rem" }}>{"Date"}</TableCell>
            <TableCell>{"Description"}</TableCell>
            <TableCell align={"right"} sx={{ width: "9rem" }}>
              {"Due"}
            </TableCell>
            <TableCell sx={{ width: "2rem", px: 0 }} />
            <TableCell align={"right"} sx={{ width: "9rem", ...besideIcon }}>
              {"Received"}
            </TableCell>
            <TableCell sx={{ width: "2rem", px: 0 }} />
            <TableCell align={"right"} sx={{ width: "9rem" }}>
              {"Balance"}
            </TableCell>
            <TableCell align={"center"} sx={{ width: "8rem" }}>
              {"Type"}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {group.rows.map((row) => (
            <EntryRow key={row.id} row={row} onRequestEdit={onRequestEdit} />
          ))}
          <TotalsRow totals={group.totals} status={group.status} />
        </TableBody>
      </Table>
    </TableContainer>
  </Box>
);

// One titled ledger per layer
const PaymentsLedgerTable = ({ boundLayers, paymentsMade, onRequestEdit }) => {
  const groups = useMemo(
    () => support.ledgerGroups(boundLayers, paymentsMade),
    [boundLayers, paymentsMade]
  );

  // The payments API doesn't return the paper provider, so it's read from the layers already in the store
  const resolvedLayers = useSelector(pricingSelectors.selectResolvedLayers);
  const { data: reinsurersConfig } = staticDataService.useConfigQuery(
    "reinsurers"
  );
  const paperProviderOf = (layerId) =>
    support.paperProviderName(
      (resolvedLayers ?? []).find(
        (l) => String(l?.layerId ?? l?.id) === String(layerId)
      )?.paperProvider,
      reinsurersConfig?.reinsurers
    );

  if (groups.length === 0) {
    return <EmptyLedger />;
  }

  return (
    <Stack spacing={4}>
      {groups.map((group) => (
        <LayerLedger
          key={group.id}
          title={[group.layerName, paperProviderOf(group.id)]
            .filter(Boolean)
            .join(" · ")}
          group={group}
          onRequestEdit={onRequestEdit}
        />
      ))}
    </Stack>
  );
};

export default PaymentsLedgerTable;
